import { motion } from 'motion/react';
import { Tabs as TabsPrimitive } from 'radix-ui';
import { type ComponentProps, createContext, use, useId, useState } from 'react';

import { cn } from '../lib/cn.ts';
import { TRANSITION } from '../lib/motion.tsx';

/** Выбранная вкладка и имя подчёркивания: у каждого набора вкладок своё, чтобы оно не перелетало между ними. */
const TabsState = createContext<{ readonly value: string | undefined; readonly indicator: string } | null>(
  null,
);

/** Набор вкладок: управляемый (`value`) или со своим состоянием (`defaultValue`). */
export function Tabs({
  value,
  defaultValue,
  onValueChange,
  ...props
}: ComponentProps<typeof TabsPrimitive.Root>) {
  const [own, setOwn] = useState(defaultValue);
  const indicator = useId();
  const current = value ?? own;
  return (
    <TabsState value={{ value: current, indicator }}>
      <TabsPrimitive.Root
        {...props}
        {...(current === undefined ? {} : { value: current })}
        onValueChange={(next) => {
          setOwn(next);
          onValueChange?.(next);
        }}
      />
    </TabsState>
  );
}

export function TabsList({ className, ...props }: ComponentProps<typeof TabsPrimitive.List>) {
  return (
    <TabsPrimitive.List
      className={cn(
        // Линия — inset-тенью, не бордером: активная черта вкладки ложится на неё без перекрытия,
        // иначе overflow-x-auto включал бы и вертикальный скролл на этот 1.5px.
        'flex gap-4 overflow-x-auto shadow-[inset_0_-1.5px_0_0_var(--g-border-soft)]',
        className,
      )}
      {...props}
    />
  );
}

/** Вкладка: активная подчёркнута акцентом; подчёркивание переезжает к новой вкладке. */
export function TabsTrigger({
  className,
  children,
  value,
  ...props
}: ComponentProps<typeof TabsPrimitive.Trigger>) {
  const state = use(TabsState);
  const active = state !== null && state.value === value;
  return (
    <TabsPrimitive.Trigger
      value={value}
      className={cn(
        'relative whitespace-nowrap pb-1.5 font-bold text-muted transition-colors hover:text-text',
        'data-[state=active]:text-text',
        className,
      )}
      {...props}
    >
      {children}
      {active ? (
        <motion.span
          aria-hidden="true"
          layoutId={state.indicator}
          transition={TRANSITION.layout}
          className="absolute inset-x-0 bottom-0 h-[2.5px] bg-accent"
        />
      ) : null}
    </TabsPrimitive.Trigger>
  );
}

/** Содержимое вкладки проявляется при переключении. */
export function TabsContent({ className, ...props }: ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      className={cn('pt-3 outline-none data-[state=active]:animate-fade-in', className)}
      {...props}
    />
  );
}
